import React, { useState, useMemo } from "react";
import { Badge, Flex } from "antd";
import {
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Line,
  ComposedChart,
} from "recharts";
import { TRANSACTION_STATUS, LedgerDto, LEDGER_CATEGORY } from "dtos/ledger.dto";
import { formatMoney } from "utils/functions";

const SERIES = [
  { key: "creditor_total" as const, label: "Creditor", color: "#3b82f6", grad: "gradCreditor" },
  { key: "program_fee" as const, label: "Commission", color: "#10b981", grad: "gradProgram" },
  { key: "service_fee" as const, label: "Service", color: "#f59e0b", grad: "gradService" },
  { key: "legal_fee" as const, label: "Legal", color: "#ef4444", grad: "gradLegal" },
  // { key: "amount" as const, label: "Total", color: "#8b5cf6", grad: "gradTotal" },
];

type SeriesKey = (typeof SERIES)[number]["key"];
const STATUS_BADGE: Record<TRANSACTION_STATUS, "success" | "processing" | "warning" | "default" | "error"> = {
  [TRANSACTION_STATUS.PAID]: "success",
  [TRANSACTION_STATUS.PROCESSING]: "processing",
  [TRANSACTION_STATUS.PENDING]: "warning",
  [TRANSACTION_STATUS.PROPOSED]: "default",
  [TRANSACTION_STATUS.FAILED]: "error",
  [TRANSACTION_STATUS.CANCELLED]: "error",
  [TRANSACTION_STATUS.PAUSED]: "warning",
  [TRANSACTION_STATUS.INDEFINITE_PAUSE]: "warning",
};

type ChartPoint = {
  week: number;
  creditor_total: number | undefined;
  program_fee: number | undefined;
  service_fee: number;
  legal_fee: number;
  /** Raw `amount` field (for tooltip) */
  amount: number;
  /** Raw `paid` field (for tooltip) */
  paid: number;
  /** Status of the first (or only) entry for this week */
  status: TRANSACTION_STATUS;
  category: LEDGER_CATEGORY;
};

function buildChartData(entries: LedgerDto[], safe = true): ChartPoint[] {
  const byWeek = new Map<number, ChartPoint>();
  let programStart = -1;
  let programEnd = -1;
  let creditorStart = -1;
  let creditorEnd = -1;

  for (const entry of entries.sort((a, b) => a.week - b.week)) {
    if (entry.distribution.program_fee > 0) {
      if (programStart === -1) programStart = entry.week;
      programEnd = entry.week;
    }
    if (entry.distribution.creditor_total > 0) {
      if (creditorStart === -1) creditorStart = entry.week;
      creditorEnd = entry.week;
    }
  }

  for (const e of entries) {
    if (e.category === LEDGER_CATEGORY.SKIP) continue;
    const existing = byWeek.get(e.week);
    const d = e.distribution;
    if (safe) {
      if (d.creditor_total < 0) {
        e.amount += Math.abs(d.creditor_total);
        d.creditor_total = 0;
      }
      if (d.program_fee < 0) {
        e.amount += Math.abs(d.program_fee);
        d.program_fee = 0;
      }
    }

    if (existing) {
      existing.creditor_total ??= 0;
      existing.creditor_total += d.creditor_total;
      existing.program_fee ??= 0;
      existing.program_fee += d.program_fee;
      existing.service_fee += d.service_fee;
      existing.legal_fee += d.legal_fee;
      existing.amount += e.amount;
      existing.paid += e.paid;
    } else {
      byWeek.set(e.week, {
        week: e.week,
        creditor_total: d.creditor_total,
        program_fee: d.program_fee,
        service_fee: d.service_fee,
        legal_fee: d.legal_fee,
        amount: e.amount,
        paid: e.paid,
        status: e.status,
        category: e.category,
      });
    }
  }

  return Array.from(byWeek.values())
    .sort((a, b) => a.week - b.week)
    .map((value) => {
      const step = value.category === LEDGER_CATEGORY.MONTHLY ? 4 : 1;

      if (programStart !== -1 && value.week < programStart - step) value.program_fee = undefined;
      else if (programEnd !== -1 && value.week > programEnd + step) value.program_fee = undefined;

      if (creditorStart !== -1 && value.week < creditorStart - step) value.creditor_total = undefined;
      else if (creditorEnd !== -1 && value.week > creditorEnd + step) value.creditor_total = undefined;

      value.week /= step;

      return value;
    });
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active: boolean;
  label: string;
  payload: { payload?: ChartPoint }[];
}) => {
  if (!active || !payload[0]?.payload) return null;
  const point: ChartPoint = payload[0]?.payload;
  const distTotal = SERIES.reduce((s, sr) => s + (point?.[sr.key] ?? 0), 0);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "12px 16px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.07)",
        minWidth: 210,
        fontFamily: "inherit",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
          paddingBottom: 8,
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
          {point.week
            ? point.category === LEDGER_CATEGORY.MONTHLY
              ? `Month ${label}`
              : `Week ${label}`
            : "Initial Payment"}
        </span>
        {point?.status && (
          <Badge
            status={STATUS_BADGE[point.status]}
            text={
              <span style={{ fontSize: 11, color: "#9ca3af", textTransform: "capitalize" }}>
                {point.status.replace(/_/g, " ")}
              </span>
            }
          />
        )}
      </div>
      {SERIES.map((s) => {
        const val = point?.[s.key] ?? 0;
        const pct = distTotal > 0 ? Math.round((val / distTotal) * 100) : 0;
        return (
          <div
            key={s.key}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 20,
              marginBottom: 5,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151" }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: s.color,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              {s.label}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: "#c4c9d4" }}>{pct}%</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>{formatMoney(val, false)}</span>
            </span>
          </div>
        );
      })}
      <div
        style={{
          borderTop: "1px solid #f3f4f6",
          marginTop: 8,
          paddingTop: 8,
          display: "flex",
          justifyContent: "space-between",
          fontSize: 13,
          fontWeight: 700,
          color: "#111827",
        }}
      >
        <span>Total Amount</span>
        <span>{formatMoney(distTotal, false)}</span>
      </div>

      {/* Paid / amount */}
      {/* {point && (
        <div style={{ marginTop: 5, fontSize: 11, color: "#9ca3af" }}>
          Collected: {formatMoney(point.paid, false)} <span style={{ color: "#d1d5db" }}>/ {fmt(point.amount)}</span>
        </div>
      )} */}
    </div>
  );
};

const LegendToggle: React.FC<{
  hidden: Set<SeriesKey>;
  onToggle: (key: SeriesKey) => void;
}> = ({ hidden, onToggle }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px" }}>
    {SERIES.map((s) => {
      const off = hidden.has(s.key);
      return (
        <button
          type="button"
          key={s.key}
          onClick={() => onToggle(s.key)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: off ? "#d1d5db" : "#6b7280",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "2px 0",
            transition: "color 0.15s",
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: off ? "#e5e7eb" : s.color,
              display: "inline-block",
              transition: "background 0.15s",
            }}
          />
          {s.label}
        </button>
      );
    })}
  </div>
);

export interface WeeklyPaymentTimelineProps {
  ledgerEntries: LedgerDto[];
  initial_weeks?: number;
  program_weeks?: number;
}

export const WeeklyPaymentTimeline: React.FC<WeeklyPaymentTimelineProps> = ({
  ledgerEntries,
  initial_weeks,
  program_weeks,
}) => {
  const [hidden, setHidden] = useState<Set<SeriesKey>>(new Set());
  const isMonthly = useMemo(
    () => ledgerEntries.some((entry) => entry.category === LEDGER_CATEGORY.MONTHLY),
    [ledgerEntries],
  );

  const chartData = useMemo(() => buildChartData(ledgerEntries), [ledgerEntries]);
  const xInterval = chartData.length <= 26 ? 3 : chartData.length <= 52 ? 8 : 13;

  const toggleSeries = (key: SeriesKey) =>
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const referenceLines: { week: number; label: string }[] = [];
  if (initial_weeks) {
    referenceLines.push({
      week: initial_weeks,
      label: "Initial Payment",
    });
  }

  if (program_weeks && program_weeks < ledgerEntries.length - 1) {
    referenceLines.push({
      week: program_weeks,
      label: "Commission Tenure",
    });
  }

  return (
    <Flex vertical>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 6, left: 4, bottom: 0 }}>
          <defs>
            {SERIES.map((s) => (
              <linearGradient key={s.grad} id={s.grad} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={s.color} stopOpacity={0.18} />
                <stop offset="95%" stopColor={s.color} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />

          <XAxis
            dataKey="week"
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            tickLine={false}
            axisLine={{ stroke: "#f3f4f6" }}
            interval={xInterval}
            tickFormatter={(v) => `${isMonthly ? "M" : "W"}${v}`}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `$${v}`}
            width={54}
          />

          <Tooltip
            content={(params) => (
              <CustomTooltip active={params.active ?? false} label={params.label} payload={params.payload ?? []} />
            )}
            cursor={{ stroke: "#e5e7eb", strokeWidth: 1.5 }}
          />
          {referenceLines.map((ref, index) => (
            <ReferenceLine
              key={ref.label}
              x={ref.week}
              stroke="#e5e7eb"
              strokeDasharray="4 3"
              label={{
                value: ref.label,
                position: "top",
                fontSize: 10,
                fill: "#c4c9d4",
                dy: 10 * (index + 1),
              }}
            />
          ))}

          {SERIES.map((s) =>
            hidden.has(s.key) ? null : (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={1.8}
                fill={`url(#${s.grad})`}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0, fill: s.color }}
              />
            ),
          )}

          <Line
            type="monotone"
            dataKey="amount"
            name="Total"
            stroke="#8b5cf6"
            strokeWidth={1.5}
            strokeDasharray="6 3"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: "#8b5cf6" }}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div style={{ marginBottom: 16 }}>
        <LegendToggle hidden={hidden} onToggle={toggleSeries} />
        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#d1d5db" }}>Click a series to toggle visibility</p>
      </div>
    </Flex>
  );
};

export default WeeklyPaymentTimeline;
